<script setup lang="ts">
import ReactionsList from '@/components/reactions/ReactionsList.vue'
import { app } from '@/composition/app'
import { ChannelState } from '@/composition/channels'
import { CommentState } from '@/composition/comments'
import { reportContent } from '@/composition/contentReport'
import { GroupState } from '@/composition/groups'
import { PostState } from '@/composition/posts'
import { ReactionState } from '@/composition/reactions'
import { UserState } from '@/composition/users'
import { checkChannelEditAccess } from '@/helper/channel.helper'
import { DEFAULT_SAFE_AREA_INSET, Platform } from '@/helper/const'
import {
  deletePost,
  getShareablePostLink,
  handleShareClick,
} from '@/helper/postCard.helper'
import { handlePostEdited } from '@/helper/posts.helper'
import { IChannel } from '@/models/Channels'
import { IPosts } from '@/models/Posts'
import { ContentType, IReaction, ReactionType } from '@/models/Reaction'
import { IUser } from '@/models/Users'
import { useAppStore } from '@/store/app'
import { getNumberAbbreviation } from '@/util/NumberUtils'
import { Capacitor, registerPlugin } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { isPwa } from '@ghl/clientportal-core/init'
import { MobileScrollContainer } from '@ghl/clientportal-core'
import {
  AlertCircleIcon,
  ChevronLeftIcon,
  DotsHorizontalIcon,
  Edit01Icon,
  LogOut01Icon,
  Pin02Icon,
  Share07Icon,
  InfoCircleIcon,
  Trash01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIDropdown,
  UIModal,
  UIModalHeader,
  UIPopover,
  UITextLgMedium,
  UITextMdMedium,
  UITextMdNormal,
  UITextSmMedium,
  UITextSmNormal,
  renderIcon,
} from '@gohighlevel/ghl-ui'
import debounce from 'lodash-es/debounce'
import {
  computed,
  defineAsyncComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useCommunitiesToastNotification } from '../../helper/communitiesToastNotification.helper'
import { allowIframeAndAttributesConfig, emitter } from '../../main'
import CommentCard from '../comments/CommentCard.vue'
import CommentButton from '../common/actionButtons/CommentButton.vue'
import LikeButton from '../common/actionButtons/LikeButton.vue'
import PopoverAvatar from '../common/avatar/PopoverAvatar.vue'
import UnpinIcon from '../common/icons/UnpinIcon.vue'
import { isChannelRestricted } from '@/helper/channel.helper'

import {
  addEventsListenerToMentions,
  openUserProfile,
  removeEventsListenersFromMentions,
} from '../helper'
import { handleLinkClick } from '@ghl/clientportal-core'
import DOMPurify from 'dompurify'
import {
  handleEditorVisibility,
  checkNativeKeyboardResize,
} from '@/util/helper'

const KeyboardEventsPlugin: any = registerPlugin('KeyboardEvents')

const appStore = useAppStore()

// lazy load components
const AttachmentsPreview = defineAsyncComponent(
  () => import('@/components/common/AttachmentsPreview.vue')
)
const PostAttachments = defineAsyncComponent(
  () => import('./PostAttachments.vue')
)
const ReactionsView = defineAsyncComponent(
  () => import('@/components/reactions/ReactionsView.vue')
)
const JoinGroupModal = defineAsyncComponent(
  () => import('@/components/common/modal/JoinGroupModal.vue')
)
const UIConfirmation = defineAsyncComponent(
  () => import('@/components/common/modal/UIConfirmation.vue')
)
const PostEditor = defineAsyncComponent(
  () => import('@/components/posts/PostEditor.vue')
)
const CommentEditor = defineAsyncComponent(
  () => import('@/components/comments/CommentEditor.vue')
)

const router = useRouter()
const route = useRoute()
const post = ref<any>(null)
const showModal = ref(true)
const showCommentEditorFlag = ref(true)
const showLikesModal = ref(false)
const postContentRef = ref<HTMLElement | null>(null)
const contentContainer = ref<HTMLElement | null>(null)
const mentionUserTooltip = ref<HTMLElement | null>(null)
const commentEditor = ref<HTMLElement | null>(null)
const commentEditorHeight = ref(0)
let observer: ResizeObserver
const highlightedComment = route.query?.commentId as string
const postId = route.params.postId as string
const highlightedCommentParentId = route.query?.parentId as string
const fetchingComments = ref(false)
const loadingMoreComments = ref(false)
const showCommentEditor = ref(true)
const currentUser = ref<IUser | undefined>(undefined)
const showDeleteConfirmationModal = ref(false)
const showEditPostModal = ref(false)
const isContentScrolled = ref(false)
const isCommentAReply = ref(false)
const postNotFound = ref(false)
const notification = useCommunitiesToastNotification()
const showReportConfirmationModal = ref(false)
const { t } = useI18n()
const postChannelData = ref<IChannel | undefined>(undefined)

const sanitizedContent = computed(() => {
  return DOMPurify.sanitize(post.value?.content, allowIframeAndAttributesConfig)
})

emitter.on('add-comment', addComment)
emitter.on('delete-comment', deleteComment)
emitter.on('pwa:reply-to-comment', () => (isCommentAReply.value = true))
emitter.on('pwa:show-comment-editor', () => (showCommentEditor.value = true))
emitter.on('pwa:hide-comment-editor', () => (showCommentEditor.value = false))
emitter.on('close-postview-modal', () => (showModal.value = false))

const commentEditorBottom = ref(0)

const handlePostViewBack = () => {
  showModal.value = false
}

onMounted(async () => {
  const hasNativeKeyboardResize = await checkNativeKeyboardResize(
    KeyboardEventsPlugin
  )

  if (Capacitor.getPlatform() === Platform.IOS && !hasNativeKeyboardResize) {
    KeyboardEventsPlugin.addListener('keyboardWillShow', info => {
      handleKeyboardWillShow(info.keyboardHeight)
    })

    KeyboardEventsPlugin.addListener('keyboardWillHide', () => {
      handleKeyboardWillHide()
    })
  }

  // Add back button handler
  window.addEventListener('popstate', handlePostViewBack)

  contentContainer.value?.addEventListener('scroll', handleContentScroll)
  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.overlaysContent = true
    // @ts-ignore
    navigator.virtualKeyboard.addEventListener(
      'geometrychange',
      handleGeometryChange
    )
  } else if (Capacitor.getPlatform() !== Platform.IOS) {
    // Handle layout changes in case virtualkeyboard API is not supported
    window.visualViewport?.addEventListener(
      'resize',
      handleVisualViewportResize
    )
  }
  const [user] = await Promise.all([
    UserState.getUserByContactId(useAppStore().$state.userId),
    findPost(),
  ])
  currentUser.value = user

  // calling this function here to set the --comment-editor-height css variable to 0
  handleCommentEditorResize()
  mentionUserTooltip.value = window.document.getElementById(
    'user_profile_tooltip'
  )
  const mentions = postContentRef.value?.querySelectorAll(
    'a[data-type="mention"]'
  )
  if (mentions) {
    addEventsListenerToMentions(mentions, mentionUserTooltip.value, router)
  }

  nextTick(() => {
    // fetch commentEditor from dom
    commentEditor.value = window.document.getElementById('post-comment-editor')
    if (commentEditor.value) {
      // add a observer that will observe addition and deletion of child elements and sub trees
      observer = new ResizeObserver(handleCommentEditorResize)
      observer.observe(commentEditor.value)
    }
    // call this function to set the new value of comment-editor-height css variable
    handleCommentEditorResize()

    // Only setup intersection observer for mobile
    if (app.state.isMobile) {
      const intercept = document.querySelector('[data-scroll-intercept]')
      const header = document.getElementById('post_view_top__container')

      if (intercept && header) {
        const observer = new IntersectionObserver(([entry]) => {
          header.classList.toggle('shadow-md', !entry.isIntersecting)
        })
        observer.observe(intercept)
      }
    }
  })

  fetchingComments.value = true

  if (!CommentState.state.postComments.get(post?.value?._id)) {
    await CommentState.fetchComments(route.params.postId as string, {
      limit: 10,
    })
  }

  if (route.query?.commentId) {
    await CommentState.fetchCommentById(
      postId,
      highlightedComment,
      highlightedCommentParentId
    )
  }
  fetchingComments.value = false
  postChannelData.value = ChannelState.setCurrentChannel(
    post.value?.channelSlug
  )
  postContentRef.value?.addEventListener('click', handleLinkClick)

  // await PostState.markPostAsRead(post.value._id, post.value.channelId)
})

onBeforeUnmount(() => {
  contentContainer.value?.removeEventListener('scroll', handleContentScroll)
  postContentRef.value?.removeEventListener('click', handleLinkClick)
  window.removeEventListener('popstate', handlePostViewBack)
})

function handleGeometryChange(changeEvent) {
  const { height } = changeEvent.target.boundingRect
  commentEditorBottom.value = height
}

function handleVisualViewportResize() {
  if (app.state.isMobile) {
    if (
      window.visualViewport &&
      window.visualViewport?.height < window.innerHeight
    ) {
      // Keyboard likely is open.
      /**
       * Set Max height of the content to visual viewport size
       * Counter scroll to the top to negate safari webkit scroll due to input focus.
       * And then set the commment editor to the bottom of the visual viewport
       */
      const keyboardHeight = window.innerHeight - window.visualViewport.height

      if (!isCommentAReply.value) {
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
            left: 0,
          })
          commentEditorBottom.value = keyboardHeight
        }, 50)
      } else {
        isCommentAReply.value = false
      }
      document.body.style.position = 'fixed'
      document.body.style.overflow = 'hidden'
      const postViewModalContent = document.querySelector(
        '.post-view-modal'
      ) as HTMLElement
      const postContent = document.getElementById(
        'post-view-modal-content'
      ) as HTMLElement
      if (postViewModalContent && window.visualViewport) {
        postViewModalContent.style.maxHeight = `${window.visualViewport.height}px`
        postContent.style.maxHeight = `${window.visualViewport.height}px`
      }
      isCommentAReply.value = false // Reset
    } else if (window.visualViewport?.height == window.innerHeight) {
      // Keyboard likely is closed.
      commentEditorBottom.value = 0
      const postViewModalContent = document.querySelector(
        '.post-view-modal'
      ) as HTMLElement
      const postContent = document.getElementById(
        'post-view-modal-content'
      ) as HTMLElement
      if (postViewModalContent && window.visualViewport) {
        postViewModalContent.style.maxHeight = '100%'
        postContent.style.maxHeight = '100%'
      }
      isCommentAReply.value = false //reset
      document.body.style.position = ''
      document.body.style.overflow = ''
    }
  }
}

function handleKeyboardWillShow(keyboardHeight) {
  // Add a small delay to ensure DOM is ready
  setTimeout(() => {
    if (!isCommentAReply.value) {
      commentEditorBottom.value = keyboardHeight - DEFAULT_SAFE_AREA_INSET

      // Force layout recalculation
      document.body.style.position = 'fixed'
      document.body.style.overflow = 'hidden'

      const postViewModalContent = document.querySelector(
        '.post-view-modal'
      ) as HTMLElement
      const postContent = document.getElementById(
        'post-view-modal-content'
      ) as HTMLElement

      if (postViewModalContent && window.visualViewport) {
        postViewModalContent.style.maxHeight = `${window.visualViewport.height}px`
        postContent.style.maxHeight = `${window.visualViewport.height}px`
      }

      // Force redraw

      postViewModalContent?.offsetHeight

      postContent?.offsetHeight

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
        left: 0,
      })
    }
    isCommentAReply.value = false // Reset
  }, 100)
}

function handleKeyboardWillHide() {
  // Add a small delay to ensure proper state cleanup
  setTimeout(() => {
    commentEditorBottom.value = 0

    const postViewModalContent = document.querySelector(
      '.post-view-modal'
    ) as HTMLElement
    const postContent = document.getElementById(
      'post-view-modal-content'
    ) as HTMLElement

    if (postViewModalContent && window.visualViewport) {
      postViewModalContent.style.maxHeight = '100%'
      postContent.style.maxHeight = '100%'
    }

    // Force redraw

    postViewModalContent?.offsetHeight

    postContent?.offsetHeight

    document.body.style.position = ''
    document.body.style.overflow = 'scroll'
    document.body.style.overflowY = 'scroll'

    isCommentAReply.value = false //reset
  }, 100)
}

function handleContentScroll() {
  isContentScrolled.value =
    contentContainer?.value && contentContainer?.value?.scrollTop > 0
      ? true
      : false
}

watch(
  () => fetchingComments.value,
  newValue => {
    if (newValue === false) {
      if (route.query?.commentId) {
        // reorder the higlighted comment to top of list of comments
        CommentState.highlightComment(
          postId,
          highlightedComment,
          highlightedCommentParentId
        )
        nextTick(() => {
          // Code to execute after rendering

          const commentsCards = document.getElementById('comments_cards')
          if (commentsCards) {
            setTimeout(() => {
              commentsCards.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              })
            }, 150)
          }
        })
      }
    }
  }
)

watch(
  () => showModal.value,
  () => {
    handleEditorVisibility(showModal.value)
  },
  { immediate: true }
)

onUnmounted(() => {
  handleEditorVisibility(false)
  setTimeout(() => {
    document.body.style.removeProperty('position')
    document.body.style.overflow = ''
  }, 100)
  window.visualViewport?.removeEventListener(
    'resize',
    handleVisualViewportResize
  )
})

onBeforeUnmount(() => {
  const mentions = postContentRef.value?.querySelectorAll(
    'a[data-type="mention"]'
  )
  if (mentions) {
    removeEventsListenersFromMentions(mentions, mentionUserTooltip.value)
  }
  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.removeEventListener(
      'geometrychange',
      handleGeometryChange
    )
  }

  if (observer) {
    observer.disconnect()
  }
  CommentState.unhighlightComment(
    postId,
    highlightedComment,
    highlightedCommentParentId
  )
})

function handleCommentEditorResize() {
  if (commentEditor.value?.clientHeight != undefined) {
    commentEditorHeight.value = commentEditor.value.clientHeight
  }

  // set comment-editor height to css variable
  document.documentElement.style.setProperty(
    '--comment-editor-height',
    commentEditorHeight.value + 'px'
  )
}

async function findPost() {
  try {
    const foundPost = await PostState.fetchPost(route.params.postId as string)
    // deep cloning post to avoid issues happening when we like or comment on post
    if (foundPost) {
      post.value = JSON.parse(JSON.stringify(foundPost))
    } else {
      // post doesn't exist or is deleted
      postNotFound.value = true
    }
  } catch (error: any) {
    console.info(error)
    if (error?.response?.status === 403 || error?.response?.status === 404) {
      postNotFound.value = true
    }
  }
}

const comments: any = computed(() => {
  return CommentState.state.postComments.get(post?.value?._id)
})

const commentsNextPage = computed(() => {
  return CommentState.state.postCommentsNextPage.has(post.value._id)
})
async function loadMoreComments() {
  try {
    loadingMoreComments.value = true
    await CommentState.fetchComments(route.params.postId as string, {
      previousId: CommentState.state.postCommentsNextPage.get(
        route.params.postId as string
      ),
      limit: 30,
    })
  } catch (error) {
    console.error(error)
  } finally {
    loadingMoreComments.value = false
  }
}
function hideCommentEditor() {
  showCommentEditorFlag.value = false
}

function addComment(comment: any) {
  post.value.commentsCount += 1
  nextTick(() => {
    // Code to execute after rendering

    if (!comment?.parentId) {
      if (!app.state.isMobile) {
        const commentsCards = document.getElementById('comments_cards')
        if (commentsCards) {
          commentsCards.style.transform = 'scale(0.8)'
          commentsCards.style.transition =
            'transform 0.3s, scroll-behavior 0.2s'

          setTimeout(() => {
            commentsCards.scrollIntoView({ behavior: 'smooth', block: 'start' })
            commentsCards.style.transform = 'scale(1)'
          }, 100)
        }
      } else {
        // Use like_share_buttons id to scroll to comments, since mobile view navbar and counter scroll is causing issues.
        const commentsCards = document.getElementById('like_share_buttons')
        if (commentsCards) {
          commentsCards.style.transform = 'scale(0.8)'
          commentsCards.style.transition =
            'transform 0.3s, scroll-behavior 0.2s'

          setTimeout(() => {
            commentsCards.scrollIntoView({ behavior: 'smooth', block: 'start' })
            commentsCards.style.transform = 'scale(1)'
          }, 100)
        }
      }
    }
  })
}
function deleteComment() {
  post.value.commentsCount -= 1
}

function closeModal() {
  handleEditorVisibility(false)
  document.body.style.position = ''
  showModal.value = false
}

watch(
  () => showModal.value,
  newValue => {
    if (newValue === false) {
      const currentPath: string = router.currentRoute.value.path
      const newPath: string = currentPath.replace(/\/posts\/\w+/, '')
      const query = route.query.q

      router.push({ path: newPath, query: { q: query } })
    }
  }
)

watch(
  () => showModal.value,
  value => {
    if (value) {
      // Push state when modal opens
      window.history.pushState({ type: 'post-view' }, '', window.location.href)
    }
  }
)

const showJoinGroupModal = ref(false)

async function likePost(_event: any) {
  if (
    !UserState.state?.currentUser?.contactId ||
    !GroupState.isActiveMember()
  ) {
    showJoinGroupModal.value = true
    return
  }
  const payload: IReaction = {
    contentId: post.value._id,
    contentType: ContentType.POST,
    reactionType: ReactionType.LIKE,
    createdBy: UserState.state.currentUser.contactId,
    channelSlug: post.value.channelSlug,
  }
  const isAddReaction = await ReactionState.toggleReaction(payload, post.value)
  if (isAddReaction != undefined) {
    addOrDeleteReaction(payload, isAddReaction)
  }
}

const addOrDeleteReaction = debounce(
  async (payload: IReaction, isAddReaction: boolean) => {
    await ReactionState.addOrDeleteReaction(payload, post.value, isAddReaction)
  },
  1000
)

const authorRole = computed(() => {
  return GroupState.state.groupAdmins.get(post.value.user.contactId)?.role
})

const showPostPreview = ref(false)
const currentPostIndex = ref(0)

function updatePreview(index: number) {
  currentPostIndex.value = index
  showPostPreview.value = true
}

const showEditor = computed(
  () => appStore.isAuthenticated && GroupState.isActiveMember()
)
const PostActionOption = computed(() => {
  const menuList = [
    {
      label: isPwa() ? t('common.share') : t('common.copyLink'),
      key: 'sharePost',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Share07Icon), null, {})
      },
    },
    {
      label: t('post.reportPost'),
      key: 'reportPost',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(AlertCircleIcon), null, {})
      },
    },
  ]

  if (
    !ChannelState.state.hasAnnouncementRestriction &&
    (checkChannelEditAccess() ||
      post.value.user.slug === UserState.state.currentUser?.slug)
  ) {
    menuList.push({
      label: t('post.deletePost'),
      key: 'deletePost',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Trash01Icon), null, {})
      },
    })
    menuList.push({
      label: t('post.movePostToChannel'),
      key: 'movePostToChannel',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(LogOut01Icon), null, {})
      },
    })
  }

  if (
    !ChannelState.state.hasAnnouncementRestriction &&
    post.value.user.slug === UserState.state.currentUser?.slug
  ) {
    menuList.push({
      label: t('post.editPost'),
      key: 'editPost',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Edit01Icon), null, {})
      },
    })
  }

  if (
    GroupState.state.isGroupAdmin &&
    postChannelData.value?.visibility === 'PUBLIC'
  ) {
    if (post.value.channelPinnedIndex == undefined) {
      menuList.push({
        label: t('post.pinToChannel'),
        key: 'pinToChannel',
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(Pin02Icon), null, {})
        },
      })
    } else {
      menuList.push({
        label: t('post.unpinFromChannel'),
        key: 'unpinFromChannel',
        class: 'rounded-2xl h-6 w-6',
        icon() {
          return h(renderIcon(UnpinIcon), null, {})
        },
      })
    }
    if (post.value.homePinnedIndex == undefined) {
      menuList.push({
        label: t('post.pinToHome'),
        key: 'pinToHome',
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(Pin02Icon), null, {})
        },
      })
    } else {
      menuList.push({
        label: t('post.unpinFromHome'),
        key: 'unpinFromHome',
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(UnpinIcon), null, {})
        },
      })
    }
  }

  return menuList
})

async function sharePost() {
  if (isPwa()) {
    const shareablePostLink = getShareablePostLink(post.value, router)
    await Share.share({
      text: shareablePostLink,
    }).catch(error => {
      console.error(error)
    })
  } else {
    await handleShareClick(post.value, router)
    notification.create({
      title: t('common.linkCopied'),
      type: 'success',
      duration: 3000,
    })
  }
}

async function handlePostAction(val: string) {
  switch (val) {
    case 'deletePost':
      nextTick(() => {
        showDeleteConfirmationModal.value = true
      })
      break
    case 'pinToChannel':
      await PostState.pinPostToChannelTimeline(
        post.value._id as string,
        post.value.channelSlug
      )
      findPost()
      notification.create({
        title: t('post.notifications.pinned'),
        type: 'success',
        duration: 3000,
      })
      break
    case 'unpinFromChannel':
      await PostState.unpinPostFromChannelTimeline(
        post.value._id as string,
        post.value.channelSlug
      )
      findPost()
      notification.create({
        title: t('post.notifications.unpinned'),
        type: 'success',
        duration: 3000,
      })
      break
    case 'pinToHome':
      await PostState.pinPostToHomeTimeline(
        post.value._id as string,
        post.value.channelId
      )
      findPost()
      notification.create({
        title: t('post.notifications.pinned'),
        type: 'success',
        duration: 3000,
      })
      break
    case 'unpinFromHome':
      await PostState.unpinPostFromHomeTimeline(
        post.value._id as string,
        post.value.channelId
      )
      findPost()
      notification.create({
        title: t('post.notifications.unpinned'),
        type: 'success',
        duration: 3000,
      })
      break
    case 'editPost':
      nextTick(() => {
        showEditPostModal.value = true
      })
      break
    case 'sharePost':
      await sharePost()
      break
    case 'reportPost':
      showReportConfirmationModal.value = true
      break
    case 'movePostToChannel':
      emitter.emit('open-move-post-to-channel-modal', post.value)
      break
  }
  // showPostActionDrawer.value = false
}

async function reportPost(post: IPosts) {
  try {
    await reportContent(
      post._id as string,
      ContentType.POST,
      useAppStore().$state.groupId,
      useAppStore().$state.locationId,
      UserState.state?.currentUser?.contactId || '',
      UserState.state?.currentUser?.fullName || ''
    )
    notification.create({
      title: t('post.notifications.reported'),
      type: 'success',
      duration: 3000,
    })
  } catch {
    notification.create({
      title: 'Something went wrong!',
      type: 'error',
      duration: 3000,
    })
  } finally {
    showReportConfirmationModal.value = false
  }
}

function closeEditPostModal() {
  showEditPostModal.value = false
}

function onLikesClicked() {
  showLikesModal.value = true
}

function openChannelTimeline(event: any) {
  router.push({
    name: 'channel-overview',
    params: {
      channelSlug: post.value?.channelSlug,
    },
  })
  event.stopPropagation()
}

function updatePostInPostView(updatedPost) {
  // Update the local ref post with the updated post
  const newPost = { ...post.value, ...updatedPost }
  post.value = newPost
}
</script>
<template>
  <div
    class="modal-parent sticky top-0 flex w-full select-none overflow-hidden"
  >
    <UIConfirmation
      v-if="postNotFound"
      :show="showModal"
      :show-actions="false"
      type="error"
      :title="t('post.postNotFound')"
      button-type="error"
      :message="t('post.postNotFoundMessage')"
      @update:show="showModal = $event"
    />
    <UIModal
      v-else-if="post?._id"
      :show="showModal"
      class-name="post-view-modal flex h-full w-full rounded-none md:rounded-xl md:max-h-[90vh] md:h-auto absolute top-[58px] left-0 right-0"
      :auto-focus="true"
      :z-index="2000"
      :width="791"
      @update:show="closeModal"
      @on-esc="closeModal"
      @on-mask-click="closeModal"
      @touchmove="
        event => {
          event.preventDefault()
        }
      "
    >
      <template #header>
        <UIModalHeader id="modal-header" ref="postViewHeader" type="custom">
          <template #customHeader>
            <div
              id="post_view_top__container"
              class="relative flex flex-row items-start justify-between bg-communities-background text-communities-font-secondary transition-shadow duration-200"
              :class="[
                app.state.isMobile
                  ? 'fixed left-0 top-0 z-50 h-[61px] w-full border-b border-communities-border bg-white px-3 pb-3 pt-2 before:absolute before:inset-0 before:-z-10 before:bg-white before:content-[\'\']'
                  : 'mx-3 mb-4',
                isContentScrolled && 'shadow-md',
              ]"
            >
              <div
                class="user-profile text-md relative flex w-full max-w-[90%] items-center justify-start"
              >
                <div
                  class="backbutton mr-3 flex cursor-pointer items-center md:hidden"
                  tabindex="0"
                  role="button"
                  aria-label="Back"
                  @click="closeModal"
                  @keyup.enter="closeModal"
                >
                  <ChevronLeftIcon
                    class="h-6 w-6 text-communities-font-secondary"
                  />
                </div>
                <div class="cursor-pointer">
                  <PopoverAvatar :user="post?.user" :user-role="authorRole" />
                </div>
                <div class="flex flex-col gap-[0.125rem] truncate px-3">
                  <div class="flex w-full items-center truncate">
                    <UITextMdMedium
                      :class="{
                        'hover:underline': post.user && post.user.fullName,
                      }"
                      class="line-clamp-1 cursor-pointer text-communities-font-primary"
                      @click.stop="
                        openUserProfile(post.user.slug, route, router)
                      "
                    >
                      {{
                        (post.user && post.user.fullName) || 'Deleted user'
                      }}</UITextMdMedium
                    >
                  </div>
                  <div class="flex font-light text-communities-font-secondary">
                    <UITextSmNormal
                      class="whitespace-nowrap text-communities-font-secondary"
                      >{{ post.relativeTime }}</UITextSmNormal
                    >
                    <UITextSmNormal
                      v-if="!route.params?.channelSlug"
                      class="whitespace-nowrap px-1 text-communities-font-secondary lg:block"
                    >
                      in
                    </UITextSmNormal>
                    <UITextSmNormal
                      v-if="!route.params?.channelSlug"
                      class="min-w-0 flex-shrink cursor-pointer truncate font-semibold text-communities-font-secondary hover:underline lg:block"
                      @click="openChannelTimeline"
                    >
                      {{ post.channelName }}
                    </UITextSmNormal>
                  </div>
                </div>
              </div>
              <UIDropdown
                id="post-action-dropdown"
                :options="PostActionOption"
                placement="bottom-end"
                class="w-20 rounded-3xl"
                @select="handlePostAction"
              >
                <div
                  id="post-action-dropdown-trigger"
                  class="flex h-8 w-8 items-center justify-center rounded-full hover:bg-communities-surface"
                  @click.stop=""
                >
                  <DotsHorizontalIcon
                    class="w-5 text-communities-font-secondary"
                  />
                </div>
              </UIDropdown>
            </div>
          </template>
        </UIModalHeader>
      </template>
      <template #footer>
        <div
          id="post-comment-editor"
          class="z-11 mx-3 h-auto flex-grow bg-communities-background"
          :class="app.state.isMobile ? 'fixed w-full' : 'sticky'"
          style="z-index: 11"
        >
          <!-- //TODO change this to loggedIn user -->

          <CommentEditor
            v-if="
              (!app.state.isMobile && showEditor) ||
              (app.state.isMobile && showEditor && showCommentEditor)
            "
            :comment-editor-bottom="commentEditorBottom"
            :user="currentUser"
            @update:show="hideCommentEditor"
          />
        </div>
      </template>
      <div id="post-view-modal-content">
        <div
          ref="contentContainer"
          class="relative flex flex-col"
          :class="{
            'post-content-scroll': !app.state.isMobile,
            'h-full max-h-[100vh] overflow-y-auto pt-[61px]':
              app.state.isMobile,
          }"
          @scroll="handleContentScroll"
        >
          <MobileScrollContainer>
            <template #scroll-slot>
              <div
                class="border-0 border-b border-solid border-communities-border"
              >
                <div
                  ref="postContentRef"
                  :class="app.state.isMobile ? 'px-4 ' : 'mx-3'"
                >
                  <div
                    v-if="app.state.isMobile"
                    data-scroll-intercept
                    class="pointer-events-none h-[1px] w-full"
                  />
                  <UITextLgMedium
                    class="post-title mt-2 pb-2 text-communities-font-primary md:text-2xl"
                  >
                    {{ post && post.title }}
                  </UITextLgMedium>

                  <UITextMdNormal
                    class="content-description whitespace-pre-wrap text-communities-font-primary"
                    v-html="sanitizedContent"
                  />
                </div>
                <div
                  v-if="post?.attachments && post.attachments.length > 0"
                  class="scrollbar-hide flex overflow-x-scroll py-2"
                  :class="app.state.isMobile ? 'px-4' : 'mx-3'"
                >
                  <div v-for="(file, index) in post.attachments" :key="file.id">
                    <PostAttachments
                      :file="file"
                      :index="index"
                      class="mr-4"
                      :total-attachments="post?.attachments?.length"
                      @update:show="updatePreview"
                    />
                  </div>
                </div>
                <div
                  id="like_share_buttons"
                  class="item-stretch flex gap-4 py-1 text-sm text-communities-font-secondary"
                  :class="
                    app.state.isMobile &&
                    'mt-4 border-0 border-t border-solid border-communities-border !py-2 px-4'
                  "
                >
                  <div class="flex items-center justify-center">
                    <LikeButton
                      :content="post"
                      :like-action="likePost"
                      class="py-2 font-bold"
                      :class="!app.state.isMobile && 'ml-3'"
                    />
                    <div
                      class="-translate-x-3 text-start text-communities-font-secondary hover:rounded-full hover:text-communities-font-primary"
                    >
                      <UIPopover
                        placement="bottom-start"
                        :show-arrow="false"
                        :show="app.state.isMobile ? false : undefined"
                      >
                        <template #trigger>
                          <UITextSmMedium
                            class="cursor-pointer p-2 font-medium"
                            @click.stop="onLikesClicked"
                          >
                            {{
                              getNumberAbbreviation(post.reactions?.LIKE || 0)
                            }}
                          </UITextSmMedium>
                        </template>
                        <ReactionsList
                          :content-id="post._id"
                          :reaction-type="ReactionType.LIKE"
                          :content-reactions-count="post.reactions?.LIKE || 0"
                        />
                      </UIPopover>
                    </div>
                    <div v-if="showLikesModal">
                      <ReactionsView
                        :content-id="post._id"
                        :reaction-type="ReactionType.LIKE"
                        :content-reactions-count="post.reactions?.LIKE || 0"
                        :show-modal="showLikesModal"
                        @update:show="showLikesModal = false"
                      />
                    </div>
                  </div>
                  <div class="flex items-center pr-4">
                    <CommentButton class="py-2 pr-4 font-bold" />
                    <UITextSmMedium class="p-1 text-center font-medium">
                      {{ getNumberAbbreviation(post?.commentsCount || 0) }}
                    </UITextSmMedium>
                  </div>
                </div>
              </div>
              <div
                id="comments_cards"
                class="mx-3 my-3"
                :style="
                  app.state.isMobile &&
                  `padding-bottom: ${commentEditorBottom + 120}px!important`
                "
              >
                <CommentCard
                  v-for="comment in comments"
                  v-if="!fetchingComments"
                  :key="comment._id"
                  class=""
                  :comment="comment"
                  :mention-user-tooltip="mentionUserTooltip"
                  :parent-post-id="post._id"
                  :expand-replies="comment._id === route.query?.parentId"
                  @show:reply-comment-editor="
                    showCommentEditor = !showCommentEditor
                  "
                />
                <div
                  v-if="!fetchingComments && commentsNextPage"
                  class="flex items-center justify-start pl-4 text-communities-font-secondary"
                >
                  <UIButton
                    id="show-comment_editor__btn"
                    :text="true"
                    class="font-sans"
                    :loading="loadingMoreComments"
                    @click="loadMoreComments()"
                  >
                    <div class="p-2 text-left font-sans text-xs">
                      {{ t('post.loadMoreComments') }}
                    </div>
                  </UIButton>
                </div>
              </div>
            </template>
          </MobileScrollContainer>
        </div>
      </div>
      <AttachmentsPreview
        :show-modal="showPostPreview"
        :file="post?.attachments && post.attachments[currentPostIndex]"
        :current-index="currentPostIndex"
        :total-attachments="post?.attachments?.length"
        @update:close="showPostPreview = false"
        @update:show="updatePreview"
      />
    </UIModal>
  </div>
  <JoinGroupModal
    :show="showJoinGroupModal"
    @update:show="showJoinGroupModal = false"
  />
  <PostEditor
    v-if="showEditPostModal"
    :show-editor="showEditPostModal"
    :auto-focus="true"
    :post="post"
    @update:show="closeEditPostModal"
    @update:edited="
      updatedPost => {
        updatePostInPostView(updatedPost)
        handlePostEdited(updatedPost)
      }
    "
  />
  <UIConfirmation
    :show="showDeleteConfirmationModal"
    type="error"
    :title="t('post.deletePost')"
    button-type="error"
    :message="t('post.deletePostConfirmation')"
    @update:show="showDeleteConfirmationModal = $event"
    @update:confirmed="deletePost(post)"
  />
  <UIConfirmation
    :show="showReportConfirmationModal"
    type="error"
    :title="t('post.reportPost')"
    button-type="error"
    :message="t('post.reportPostConfirmation')"
    @update:show="showReportConfirmationModal = $event"
    @update:confirmed="reportPost(post)"
  />
</template>
<style>
.custom-modal-post-view-width {
  min-width: 375px;
  max-width: 848px;
  width: auto !important;
}
a:active,
a:focus {
  outline: 0;
  border: none;
  -moz-outline-style: none;
}
</style>
<style scoped>
.scrollbar-hide {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.scrollbar-hide::-webkit-scrollbar {
  width: 3px;
}

.scrollbar-hide::-webkit-scrollbar-thumb {
  background-color: transparent;
}

.scrollbar-hide::-webkit-scrollbar-track {
  background-color: transparent;
}

.post-content-scroll {
  max-height: calc(100vh - 217px - var(--comment-editor-height));
  overflow-y: auto;
  overflow-x: visible !important;
  scrollbar-width: thin;
}
.post-content-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.post-content-scrollbar::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 2px;
}
.post-content-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: #c2c2c2;
  border-radius: 2px;
}

#post_view_top__container {
  background: var(--communities-background);
}
.backbutton [tabindex='0']:focus {
  outline: none;
  box-shadow: none;
}

@media (max-width: 930px) {
  #post_view_top__container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background: white;
    border-bottom: 0.75px solid var(--border);
  }

  #post_view_top__container::before {
    content: '';
    position: absolute;
    inset: 0;
    background: white;
    z-index: -1;
  }
}
</style>
<style>
.post-view-modal {
  max-width: 791px !important;
  width: 80% !important;
  padding: 24px 12px !important;
}
@media (max-width: 930px) {
  .post-view-modal {
    position: fixed !important;
    width: 100% !important;
    top: 0px !important;
    left: 0px !important;
    right: 0px !important;
    overflow-y: scroll !important;
    padding: 0px !important;
    max-width: 100% !important;
  }

  #post-view-modal-content {
    overflow-y: scroll !important;
  }
}
</style>
